import { ACID, BASE, LIGHT } from '../../palette';
import { pt, type Pt } from './geom';
import { weaveField } from './marks';
import { box, label, line, solid, type FaceSpec, type Prim } from './spec';
import { wedgeMark } from './wedge';
import { TITLE, TITLE_DESIGN, WEDGE } from './tune';

/**
 * **标题 Logo。**
 *
 * 游戏叫 `ACID SAMIZDAT`（酸性地下出版物）。
 * 项目名、包名、仓库名一律没动 —— 这是**游戏的显示名**，不是项目名。
 *
 * ## 为什么它是 spec，不是一张图
 *
 * 它走的是**和牌面、牌背完全相同的那条管线**（`spec.ts` → `painter.ts`）。
 * 于是它自动继承了：织纹、标记、色板纪律、以及调优面板。
 * 如果给它单开一条路（贴一张位图、或者写一套 DOM 排版），
 * 就又会出现"两份实现"——那正是当初 SVG 打样和 Pixi 占位并存时踩过的坑。
 *
 * ## 它和牌背的纽带
 *
 * 中央那个"两块没对准的版"标记是**同一个函数**（`marks.plateSigil`）出来的。
 * 牌背和 Logo 不是两张各画各的图，是同一台机器的两种印张。
 *
 * ## 排版的三个动作（没有字体文件，所以全靠这三样）
 *
 * 1. **齐宽** —— `ACID`（4 字）和 `SAMIZDAT`（8 字）撑满同一条 measure。
 *    字号比取 2 : 1 时两行天然等宽，`fitWidth` 只做收尾的微调。
 *    ⚠️ 宽度是**量出来**的，不是猜字号猜出来的：不同机器上
 *    `FONT_DISPLAY` 会落到不同字体，猜一个字号必然有的机器上超出、有的短一截。
 * 2. **套印错位** —— 两个酸色版各自朝反方向偏 1.7 单位，白的压在最上面。
 *    这正是印刷层的 `misregistration`，只不过这里做得理直气壮 ——
 *    它是**标题**，该响。
 * 3. **紧、扁、贴边** —— `fitWidth` 只缩 x 不缩 y，字形被横向压扁。
 *    （`reference-board.md` 的 Akira 一条：标题卡的排版感，短、粗、贴边。）
 */
export function titleFace(): FaceSpec {
  const w = TITLE_DESIGN.width;
  const h = TITLE_DESIGN.height;
  const cx = w / 2;

  const band: readonly Pt[] = [
    pt(0, TITLE.bandTop),
    pt(w, TITLE.bandTop),
    pt(w, TITLE.bandBottom),
    pt(0, TITLE.bandBottom),
  ];

  // ⚠️ 印张带里的织纹要比牌背**更淡**：标题是给人读的，
  //    织纹一重，两行大字就开始和背景打架
  const weave = weaveField(band, {
    linePitch: 5.6,
    lineAngle: 30,
    lineDuty: 0.08,
    lineAlpha: 0.26,
    dotPitch: 5,
    dotAngle: 75,
    dotCoverage: 0.1,
    dotAlpha: 0.26,
  });

  /** 一个色版的一行字 */
  const plate = (
    text: string,
    size: number,
    y: number,
    dx: number,
    dy: number,
    color: number,
    alpha: number,
  ): Prim =>
    label(text, cx + dx, y + dy, {
      size,
      color,
      role: 'display',
      weight: 900,
      tracking: 0.5,
      anchor: 'middle',
      fitWidth: TITLE.measure,
      alpha,
    });

  const wordmark: Prim[] = [
    // 先印的两个酸色版，各自朝反方向错位 —— 露在白字的左上和右下
    plate('ACID', TITLE.line1Size, TITLE.line1Y, -TITLE.misX, TITLE.misY, ACID.lime, 0.95),
    plate('SAMIZDAT', TITLE.line2Size, TITLE.line2Y, -TITLE.misX, TITLE.misY, ACID.lime, 0.95),
    plate('ACID', TITLE.line1Size, TITLE.line1Y, TITLE.misX, -TITLE.misY, ACID.cyan, 0.95),
    plate('SAMIZDAT', TITLE.line2Size, TITLE.line2Y, TITLE.misX, -TITLE.misY, ACID.cyan, 0.95),
    // 最后是主版
    plate('ACID', TITLE.line1Size, TITLE.line1Y, 0, 0, LIGHT.base, 1),
    plate('SAMIZDAT', TITLE.line2Size, TITLE.line2Y, 0, 0, LIGHT.base, 1),
  ];

  // 标记：**一枚红楔子**。和 favicon 是同一个函数，只是比例不同。
  // ⚠️ 它是"标记"不是"动作" —— 见 wedge.ts 顶上那张分工表
  const sigil = wedgeMark({
    x: cx,
    y: TITLE.sigilY,
    length: TITLE.wedgeLength,
    backRatio: WEDGE.titleBackRatio,
    angleDeg: WEDGE.titleAngle,
    color: WEDGE.red,
  });

  return {
    design: { width: w, height: h },
    // ⚠️ 透明底：标题要能压在**活着的**标量场背景上。
    //    给它刷一块黑底就把它焊死在背景上了
    ground: { t: 'none' },
    accent: ACID.lime,
    prims: [
      box(0, TITLE.bandTop, w, TITLE.bandBottom - TITLE.bandTop, solid(BASE.raised, 0.94)),
      ...weave,
      // 印张带的上下刀口
      line([pt(0, TITLE.bandTop), pt(w, TITLE.bandTop)], {
        width: 1,
        color: ACID.lime,
        alpha: 0.85,
      }),
      line([pt(0, TITLE.bandBottom), pt(w, TITLE.bandBottom)], {
        width: 1,
        color: ACID.lime,
        alpha: 0.85,
      }),
      ...sigil,
      ...wordmark,
      label('SELF-PUBLISHED · UNAUTHORISED EDITION', cx, 206, {
        size: 8,
        color: LIGHT.dim,
        tracking: 3.2,
        anchor: 'middle',
      }),
      label('REV 0', 6, 14, { size: 7, color: LIGHT.dim, tracking: 2 }),
      label('LOT 0000', w - 6, 14, { size: 7, color: LIGHT.dim, tracking: 2, anchor: 'end' }),
    ],
    // 错位是**明确画进去的**（上面那三个色版），不走印刷层 —— 否则会叠两次
    press: false,
  };
}

/**
 * **只要文字部分** —— 给标题页用。
 *
 * 和 `titleFace()` 的区别只有一个：**去掉印张带**（黑底、织纹、上下刀口）
 * 和中央那个"没对准的版"标记，只留字。
 *
 * ## 为什么标题页需要这个
 *
 * `titleFace()` 里那条 `ground: { t: 'none' }` 的注释说得很清楚：标题本来就是
 * 设计成"压在**活着的**标量场背景上"的，所以它自带一块 `BASE.raised` 的印张带
 * 把字托住 —— 在调试页那种纯黑底上这是对的。
 *
 * 但标题页后面跑的就是那个标量场。再叠一块不透明的印张带，等于把活背景盖死，
 * 而"活背景"恰恰是这一屏最值钱的东西。所以标题页取**没带子的那一版**。
 *
 * ⚠️ 用 `p.k === 'text'` 过滤，而不是按内容挑 —— 判据是**图元的种类**，
 * 将来往 `titleFace()` 里加新的装饰不会被这里悄悄漏掉或误伤。
 */
export function titleWordmarkFace(): FaceSpec {
  const full = titleFace();
  return {
    ...full,
    prims: full.prims.filter((prim) => prim.k === 'text'),
  };
}
